import dayjs from "dayjs";
import { Task, ViewMode } from "@thepocman/gantt-task-react";

export const COLORS = [
  "#0C98D0",
  "#ffbb54",
  "#C275A1",
  "#4CAF50",
  "#FF7043",
  "#7E57C2",
  "#26A69A",
  "#EF5350",
  "#FFA726",
  "#78909C",
];

export const DOT_COLUMN_WIDTH = 42;
export const FALLBACK_COLUMN_WIDTH = 160;
export const DATE_FORMAT = "YYYY-MM-DD";

export type TimelineLegendItem = {
  key: string;
  label: string;
  borderColor: string;
  fillColor: string;
};

export type TimelineUtils = {
  defaultVisibleColumns: readonly string[];
  columnLabels: Record<string, string>;
  getTableColumnWidth: (column: string) => number;
  getLegendFillColor: (value: string) => string;
  getLegendBorderColor: (value: string) => string;
  getLegendBorderColorFromFill: (fillColor: string) => string;
  getAllLegendItems: (keys?: string[]) => TimelineLegendItem[];
};

export function getColumnWidth(viewMode: ViewMode): number {
  if (viewMode === ViewMode.Year) return 120;
  if (viewMode === ViewMode.Month) return 88;
  return 55;
}

export function getTimelineOffsetDate(
  startDate: Date,
  offset: number,
  viewMode: ViewMode,
) {
  if (viewMode === ViewMode.Day) {
    return dayjs(startDate).add(offset, "day").toDate();
  }

  if (viewMode === ViewMode.Month) {
    return dayjs(startDate).add(offset, "month").toDate();
  }

  return dayjs(startDate).add(offset, "year").toDate();
}

export function getTimelineScrollLeft(
  targetDate: Date,
  startDate: Date,
  viewMode: ViewMode,
  columnWidth: number,
) {
  const diffUnit =
    viewMode === ViewMode.Day
      ? "day"
      : viewMode === ViewMode.Month
        ? "month"
        : "year";
  const index = dayjs(targetDate).diff(startDate, diffUnit);
  const currentDate = getTimelineOffsetDate(startDate, index, viewMode);
  const nextDate = getTimelineOffsetDate(startDate, index + 1, viewMode);
  const intervalWidth = nextDate.getTime() - currentDate.getTime();
  const remainder = targetDate.getTime() - currentDate.getTime();
  const percentOfInterval = intervalWidth > 0 ? remainder / intervalWidth : 0;

  return (index + percentOfInterval) * columnWidth;
}

export function getGanttTasksWithTodayAnchor(tasks: Task[]) {
  if (!tasks.length) return tasks;

  const today = new Date();
  const maxEnd = Math.max(...tasks.map((task) => task.end.getTime()));
  if (today.getTime() <= dayjs(maxEnd).add(30, "day").valueOf()) {
    return tasks;
  }

  const phantomTask: Task = {
    id: "__today_anchor__",
    name: "",
    start: today,
    end: dayjs(today).add(1, "day").toDate(),
    progress: 0,
    type: "task",
    isDisabled: true,
    isRelationDisabled: true,
    styles: {
      barBackgroundColor: "transparent",
      barBackgroundSelectedColor: "transparent",
      barProgressColor: "transparent",
      barProgressSelectedColor: "transparent",
    },
  };

  return [...tasks, phantomTask];
}
